import { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { ASSETS, CONTACT } from "../lib/brand";
import { IconMapPin } from "./icons";

export default function TourLoja() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yBg = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      ref={ref}
      data-testid="tour-section"
      className="relative bg-ink-800 text-cream py-24 sm:py-40 overflow-hidden"
    >
      {/* Background image with parallax */}
      <motion.div
        aria-hidden
        style={{ y: yBg }}
        className="absolute inset-0 z-0 opacity-25"
      >
        <img
          src={ASSETS.rack}
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </motion.div>
      <div
        aria-hidden
        className="absolute inset-0 z-[1]"
        style={{
          background:
            "linear-gradient(180deg, rgba(26,24,24,0.75) 0%, rgba(26,24,24,0.55) 55%, rgba(26,24,24,0.85) 100%)",
        }}
      />

      <div className="relative z-[2] mx-auto max-w-[1600px] px-6 sm:px-10">
        <div className="grid grid-cols-12 gap-6 lg:gap-16 items-end">
          {/* Left: editorial title */}
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.3em] text-cream/70 mb-8">
              <span className="w-8 h-px bg-brick-500" />
              <span>N.º 002 — Tour virtual</span>
            </div>

            <div className="overflow-hidden">
              <motion.h2
                initial={{ y: "110%" }}
                animate={inView ? { y: 0 } : {}}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="font-serif leading-[0.88] tracking-tight text-cream"
                style={{ fontSize: "clamp(3rem, 8vw, 8.5rem)" }}
              >
                Entre.
              </motion.h2>
            </div>
            <div className="overflow-hidden mt-2">
              <motion.h2
                initial={{ y: "110%" }}
                animate={inView ? { y: 0 } : {}}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
                className="font-serif italic font-medium leading-[0.88] tracking-tight text-brick-500"
                style={{ fontSize: "clamp(3rem, 8vw, 8.5rem)" }}
              >
                Fique à vontade.
              </motion.h2>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.9, delay: 0.55 }}
              className="mt-10 font-sans text-base sm:text-lg leading-relaxed text-cream/80 max-w-xl"
            >
              A minha loja é um recorte físico do universo desapegue — peças escolhidas
              a dedo, luz cuidada, ambiente para você olhar sem pressa.
              Um passeio guiado pelos bastidores, comigo.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.9, delay: 0.75 }}
              className="mt-10 flex flex-wrap items-center gap-x-10 gap-y-4 font-mono text-[10px] uppercase tracking-[0.3em] text-cream/70"
            >
              <span className="flex items-center gap-3">
                <IconMapPin size={14} strokeWidth={1.6} />
                {CONTACT.address.street} · {CONTACT.address.neighborhood}
              </span>
              <span className="hidden sm:inline w-8 h-px bg-cream/30" />
              <span>Ter — Sáb · Curitiba, PR</span>
            </motion.div>
          </div>

          {/* Right: video invitation card */}
          <div className="col-span-12 lg:col-span-5 lg:pl-8">
            <motion.a
              href={CONTACT.tour}
              target="_blank"
              rel="noreferrer"
              data-testid="tour-cta"
              aria-label="Assistir tour pela loja no Instagram"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              className="group relative block bg-cream text-ink-800 border border-cream overflow-hidden"
            >
              {/* Preview image */}
              <div className="relative aspect-[4/5] overflow-hidden bg-cream-200">
                <img
                  src={ASSETS.hangers}
                  alt="Prévia — tour pela loja"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                />
                {/* subtle dark gradient */}
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(26,24,24,0) 40%, rgba(26,24,24,0.55) 100%)",
                  }}
                />

                {/* Play button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center">
                    {/* pulsing ring */}
                    <motion.span
                      aria-hidden
                      className="absolute inset-0 rounded-full border border-cream"
                      animate={{
                        scale: [1, 1.35, 1],
                        opacity: [0.6, 0, 0.6],
                      }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                    />
                    <motion.span
                      aria-hidden
                      className="absolute inset-0 rounded-full border border-cream/60"
                      animate={{
                        scale: [1, 1.6, 1],
                        opacity: [0.4, 0, 0.4],
                      }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", delay: 0.6 }}
                    />
                    <span className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-cream text-brick-500 flex items-center justify-center group-hover:bg-brick-500 group-hover:text-cream transition-colors duration-500">
                      <svg
                        viewBox="0 0 24 24"
                        width="26"
                        height="26"
                        fill="currentColor"
                        aria-hidden="true"
                        className="ml-1"
                      >
                        <path d="M8 5.14v13.72a1 1 0 001.55.83l10.03-6.86a1 1 0 000-1.66L9.55 4.31A1 1 0 008 5.14z" />
                      </svg>
                    </span>
                  </div>
                </div>

                {/* Corner ticks */}
                <div className="absolute top-3 left-3 right-3 flex justify-between font-mono text-[9px] uppercase tracking-[0.3em] text-cream">
                  <span>● Ao vivo do atelier</span>
                  <span>HL / Loja</span>
                </div>
              </div>

              {/* Bottom band */}
              <div className="flex items-center justify-between gap-4 px-5 sm:px-6 py-5 border-t border-ink-800/15">
                <div className="flex flex-col">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brick-500">
                    Stories · destacados
                  </span>
                  <span
                    className="mt-1 font-serif italic leading-none"
                    style={{ fontSize: "clamp(1.35rem, 1.7vw, 1.75rem)" }}
                  >
                    Faça um tour pela loja
                  </span>
                </div>
                <span className="font-serif italic text-3xl text-brick-500 group-hover:translate-x-1 transition-transform duration-500">
                  →
                </span>
              </div>
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
}
